import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  TextField,
  useMediaQuery,
} from "@mui/material";
import Header from "../../components/Header";
import { Bar, Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { useTheme } from "@mui/material/styles";
import { getTurnosAnalytics } from "../../services/turnoService";
import { useSelector } from "react-redux";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

const Analytics = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { userId } = useSelector((s) => s.user);
  const desdeRef = useRef(null);
  const hastaRef = useRef(null);
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const monthStart = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const formatDate = (d) => d.toISOString().split("T")[0];

  const [interval, setInterval] = useState("diario");
  const [dateRange, setDateRange] = useState({
    desde: formatDate(monthStart),
    hasta: formatDate(today),
  });
  const [lineData, setLineData] = useState({ labels: [], datasets: [] });
  const [pieData, setPieData] = useState({ labels: [], datasets: [] });
  const [barData, setBarData] = useState({ labels: [], datasets: [] });
  const [error, setError] = useState(null);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
  };
  const paletteColors = isDark
    ? ["#60a5fa", "#fbbf24", "#f97316", "#f43f5e", "#34d399"]
    : ["#1d4ed8", "#f59e0b", "#f97316", "#ef4444", "#10b981"];

  const loadData = async () => {
    try {
      setError(null);
      const data = await getTurnosAnalytics({
        startDate: dateRange.desde,
        endDate: dateRange.hasta,
        interval,
      });

      const backendDatasets = Array.isArray(data.line?.datasets) ? data.line.datasets : null;
      const barColorMap = new Map();
      const coloredBarDatasets = (data.bar?.datasets || []).map((ds, idx) => {
        const color = paletteColors[idx % paletteColors.length];
        barColorMap.set(ds.label, color);
        return { ...ds, backgroundColor: color };
      });

      const lineDatasets =
        backendDatasets && backendDatasets.length
          ? backendDatasets.map((ds, idx) => {
              const color = barColorMap.get(ds.label) || paletteColors[idx % paletteColors.length];
              return {
                ...ds,
                borderColor: color,
                backgroundColor: color,
              };
            })
          : [
              {
                label: "Facturación (€)",
                data: data.line?.data || [],
                borderColor: paletteColors[0],
                backgroundColor: paletteColors[0],
              },
            ];

      setLineData({
        labels: data.line?.labels || [],
        datasets: lineDatasets,
      });

      setPieData({
        labels: data.pie?.labels || [],
        datasets: [
          {
            label: "Distribución",
            data: data.pie?.data || [],
            backgroundColor: paletteColors,
          },
        ],
      });

      setBarData({
        labels: data.bar?.labels || [],
        datasets: coloredBarDatasets,
      });
    } catch (err) {
      console.error("Error cargando analíticas:", err);
      setError(err?.message || "No se pudieron cargar los datos");
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval, dateRange.desde, dateRange.hasta, userId]);

  return (
    <Box
      m="20px"
      sx={{
        height: "100%", // <- Este es crucial
        overflow: "auto",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={isMobile ? "flex-start" : "center"}
        flexDirection={isMobile ? "column" : "row"}
        gap={isMobile ? 2 : 0}
        mb={2}
      >
        <Header
          title="Analítica"
          subtitle="Gráficos de facturación e ingresos/gastos"
        />
        <Box
          display="flex"
          flexWrap={isMobile ? "wrap" : "nowrap"}
          gap={2}
          mt={{ xs: 0, md: 0 }}
          sx={{
            backgroundColor: theme.palette.custom.serviceCardBackground,
            borderRadius: 2,
            boxShadow: 3,
            p: 2,
            width: isMobile ? "100%" : "auto",
          }}
        >
          <FormControl sx={{ minWidth: isMobile ? "100%" : 140 }}>
            <InputLabel>Intervalo</InputLabel>
            <Select
              value={interval}
              label="Intervalo"
              onChange={(e) => setInterval(e.target.value)}
              size="small"
            >
              <MenuItem value="diario">Días</MenuItem>
              <MenuItem value="semanal">Semanas</MenuItem>
              <MenuItem value="mensual">Meses</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Desde"
            type="date"
            value={dateRange.desde}
            onChange={(e) =>
              setDateRange({ ...dateRange, desde: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            size="small"
            inputRef={desdeRef}
            fullWidth={isMobile}
            onClick={() => {
              if (desdeRef.current?.showPicker) desdeRef.current.showPicker();
            }}
            sx={{
              "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
              "& .MuiInputLabel-root.Mui-focused": { color: theme.palette.text.primary },
              "& .MuiInputBase-input": { color: theme.palette.text.primary },
              "& .MuiInputBase-input.Mui-focused": { color: theme.palette.text.primary },
              "& .MuiSvgIcon-root": { color: theme.palette.text.primary },
              "& input::-webkit-calendar-picker-indicator": {
                filter: isDark ? "invert(1)" : "invert(0)",
              },
            }}
          />
          <TextField
            label="Hasta"
            type="date"
            value={dateRange.hasta}
            onChange={(e) =>
              setDateRange({ ...dateRange, hasta: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            size="small"
            inputRef={hastaRef}
            fullWidth={isMobile}
            onClick={() => {
              if (hastaRef.current?.showPicker) hastaRef.current.showPicker();
            }}
            sx={{
              "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
              "& .MuiInputLabel-root.Mui-focused": { color: theme.palette.text.primary },
              "& .MuiInputBase-input": { color: theme.palette.text.primary },
              "& .MuiInputBase-input.Mui-focused": { color: theme.palette.text.primary },
              "& .MuiSvgIcon-root": { color: theme.palette.text.primary },
              "& input::-webkit-calendar-picker-indicator": {
                filter: isDark ? "invert(1)" : "invert(0)",
              },
            }}
          />
        </Box>
      </Box>

      {/* Línea de evolución */}
      <Box
        sx={{
          height: isMobile ? 260 : 300,
          backgroundColor: theme.palette.custom.serviceCardBackground,
          p: 2,
          borderRadius: 2,
          mb: 3,
        }}
      >
        <Typography variant="h4" gutterBottom>
          Evolución de Facturación
        </Typography>
        {error && (
          <Typography sx={{ color: theme.palette.error.light, mb: 1 }}>
            {error}
          </Typography>
        )}
        <Box sx={{ height: "90%" }}>
          <Line data={lineData} options={chartOptions} />
        </Box>
      </Box>

      {/* Gráficos inferiores */}
      <Box display="flex" gap={3} flexDirection={isMobile ? "column" : "row"}>
        <Box
          sx={{
            flex: 1,
            height: isMobile ? 280 : 370,
            backgroundColor: theme.palette.custom.serviceCardBackground,
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant="h4" gutterBottom>
            Distribución de Gastos e Ingresos
          </Typography>
          <Box sx={{ height: "90%" }}>
            <Pie data={pieData} options={chartOptions} />
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            height: isMobile ? 280 : 370,
            backgroundColor: theme.palette.custom.serviceCardBackground,
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant="h4" gutterBottom>
            Comparativa Mensual por Conductor
          </Typography>
          <Box sx={{ height: "90%" }}>
            <Bar data={barData} options={chartOptions} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Analytics;
